import React from "react";
import { Category } from "../processors";
import { CategorySetDisplay } from "./types";
import { useCategoryContext } from "../Context";
import { useCategoryMetaContext } from "../Context/CategoryMetaContext";
import { CategorySetToggleDisplay } from "./CategoryToggle";
import { CategorySetTabDisplay } from "./CategoryTab";
import { CategorySetSectionDisplay } from "./CategorySection";
import { CategoryDisplayType } from "../misc";
import _ from "lodash";

const defaultResolve = {
    result: [],
    displayTypes: [],
    previous: null
};

// Display categories based on their display types
export const CategoryDisplay: React.FC<CategorySetDisplay> = ({ categories }) => {
    // group categories by display type
    const { result, displayTypes } =
        categories
            ?.filter(d => d != null)
            ?.reduce<{
                result: Category[][];
                displayTypes: string[];
                previous?: Category;
            }>((ret, category, index) => {
                if (index === 0) {
                    return {
                        result: [[category]],
                        previous: category,
                        displayTypes: [category?.displayType]
                    };
                }

                if (category?.displayType === ret?.previous?.displayType) {
                    const result = ret.result;
                    result?.[result.length - 1]?.push(category);
                    const displayTypes = ret.displayTypes;
                    return {
                        result,
                        previous: category,
                        displayTypes
                    };
                }

                const result = ret.result;
                result?.push([category]);
                const displayTypes = ret.displayTypes;
                displayTypes.push(category?.displayType);
                return {
                    result,
                    previous: category,
                    displayTypes
                };
            }, defaultResolve) ?? defaultResolve;

    const CategoryDisplay: Record<CategoryDisplayType, React.FC<CategorySetDisplay>> = {
        panel: CategorySetToggleDisplay,
        section: CategorySetSectionDisplay,
        tab: CategorySetTabDisplay
    };

    return (
        <>
            {result?.map((group, index) => {
                const Display = CategoryDisplay?.[displayTypes?.[index]];
                if (!Display) return null;

                const Component = Display as React.FC<CategorySetDisplay>;
                return <Component categories={group} key={index} />;
            })}
        </>
    );
};

export const CategoryDisplayWithContext: React.FC<{}> = () => {
    const { category } = useCategoryContext();
    const { tIdToCategory } = useCategoryMetaContext();

    const children = _.orderBy(
        category?.childIds?.map(d => tIdToCategory?.[d])?.filter(d => d != null) ?? [],
        d => d.weight,
        "asc"
    );
    return <CategoryDisplay categories={children} />;
};
